/**
 * The spine — Story #5648 build v2, design stream S1 "The spine".
 *
 * One vocabulary for the zoom levels every graduated screen sits on. The
 * ROLE a person is acting as decides which levels they may open; what fills
 * a level is what they own plus what has been shared with them in that role.
 * Nothing here knows about seats, tiers, plans or credits — those are
 * licensing constructs and do not exist in this UI.
 */
export type SpineLevel =
  | 'claim'
  | 'document'
  | 'invention'
  | 'my-inventions'
  | 'entity'
  | 'portfolio'
  | 'across'

/** Zoom order, closest first. */
export const SPINE_ORDER: SpineLevel[] = ['claim', 'document', 'invention', 'my-inventions', 'entity', 'portfolio', 'across']

export const SPINE_LEVEL_LABEL: Record<SpineLevel, string> = {
  claim: 'Claim',
  document: 'Document',
  invention: 'Invention',
  'my-inventions': 'My inventions',
  entity: 'Entity',
  portfolio: 'Portfolio',
  across: 'Across portfolios',
}

/** The one question each level answers — the pill tooltips in the figures. */
export const SPINE_LEVEL_QUESTION: Record<SpineLevel, string> = {
  claim: 'Does this claim hold — what supports it, and what is the one next step on it?',
  document: 'What does this document establish, and which claims does it support?',
  invention: 'Is this invention novel, where is it in the pipeline, and what is the one next step?',
  'my-inventions': 'Across everything I own, what needs me first, and how do my inventions sit together?',
  entity: 'What has this Entity let us see, where is it in the pipeline, and what is our next action on it?',
  portfolio: "Where is the white space and the duplicated effort across everything I'm entitled to see?",
  across: 'Where does this portfolio touch other portfolios — at what rung, and who must act next?',
}

/** The role a person is ACTING AS in the current workspace. */
export type SpineRole = 'inventor' | 'collaborator' | 'aggregator' | 'organisation' | 'platform-admin'

/**
 * Levels a role may open, closest first. A page may pass its own narrower list
 * — the `levels` field on the spec — when its figure draws fewer notches.
 *
 * SETTLED by Rob, 7 Sep 2026. The approved figures disagreed with each other
 * about the inventor's window: C2 and C5 drew FOUR notches (Claim · Document
 * · Invention · My inventions) while S1, S2, S3, S4 and S4b drew TWO
 * (Invention · My inventions). The design document sides with two: a solo
 * inventor "sees Invention and My inventions — two scopes", and a claim or a
 * document is a subject inside a lens, "never a rung of its own", because
 * neither contains the other so zooming out from one to the other was a move
 * that never existed. Rob confirmed two notches everywhere — the inventor's
 * Personal-workspace rail never grows a Claim or Document rung. C2 and C5 are
 * the two screens this changes; their figures need redrawing to match.
 */
export function levelsForRole(role: SpineRole): SpineLevel[] {
  switch (role) {
    case 'aggregator':
      return ['invention', 'entity', 'portfolio', 'across']
    case 'organisation':
      return ['invention', 'my-inventions', 'entity', 'portfolio']
    case 'platform-admin':
      return SPINE_ORDER
    case 'collaborator':
    case 'inventor':
    default:
      return ['invention', 'my-inventions']
  }
}

/** Default route for a level, when a page does not pass its own link. */
export const SPINE_DEFAULT_LINK: Partial<Record<SpineLevel, string>> = {
  'my-inventions': '/projects?view=atlas',
  portfolio: '/agg?view=overview',
  across: '/agg/across',
}

/**
 * Why a level the viewer can see named but cannot open is shut. Exactly three
 * reasons exist, they are written once here, and every locked notch uses one
 * of them verbatim — a lock is always visible and always named, never a hidden
 * level and never a dead control.
 */
export type SpineLockReason = 'widest-view' | 'other-workspace' | 'view-only'

export const SPINE_LOCK_REASON: Record<SpineLockReason, string> = {
  'widest-view': 'set by your organisation\u2019s Widest view policy',
  'other-workspace': 'switch to another workspace to see this',
  'view-only': 'your role is view-only here',
}

/** "1 step waiting" / "2 steps waiting" / "" */
export function stepsWaitingLabel(n: number | undefined): string {
  if (!n || n < 1) return ''
  return n === 1 ? '1 step waiting' : `${n} steps waiting`
}
